import { HttpClient, HttpClientModule } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom } from '@angular/core';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { MatNativeDateModule } from '@angular/material/core';
import { OwlDateTimeModule, OwlNativeDateTimeModule } from '@danielmoncada/angular-datetime-picker';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { PreloadAllModules, provideRouter, withPreloading } from '@angular/router';
import { InMemoryCache, from } from '@apollo/client/core';
import { onError } from '@apollo/client/link/error';
import { removeTypenameFromVariables } from '@apollo/client/link/remove-typename';
import { CoreModule } from '@core/core.module';
import { AuthGuardService } from '@core/services/auth/auth-guard.service';
import { environment } from '@env/environment';
import { FORMLY_CONFIG, FormlyModule } from '@ngx-formly/core';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { APOLLO_OPTIONS, Apollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { APP_ROUTES } from './app-routing.module';
import { COMPONENT_CONFIG } from './core/json-schema-controls/dynamic-component-map';

export function HttpLoaderFactory(httpClient: HttpClient) {
  return new TranslateHttpLoader(httpClient, 'assets/i18n/', '.json');
}

export function formlyValidationConfig(translate: TranslateService) {
  return {
    validationMessages: [
      {
        name: 'required',
        message() {
          return translate.stream('FORM.VALIDATION.REQUIRED');
        },
      },
    ],
  };
}

const token = localStorage.getItem('token');

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    const errorMessages = [];
    for (const err of graphQLErrors) {
      try {
        // Check if this is a BAD_REQUEST error
        if (err.extensions && err.extensions['code'] === 'BAD_REQUEST') {
          // Safely access originalError and its message array
          if (
            err.extensions['originalError'] &&
            typeof err.extensions['originalError'] === 'object' &&
            err.extensions['originalError'] !== null
          ) {
            const originalError = err.extensions['originalError'];

            // Handle message array
            if (originalError['message']) {
              if (Array.isArray(originalError['message'])) {
                errorMessages.push(...originalError['message']);
              } else if (typeof originalError['message'] === 'string') {
                errorMessages.push(originalError['message']);
              }
            }
          } else {
            // Fallback if originalError doesn't exist but we still have an err.message
            if (err.message) {
              errorMessages.push(err.message);
            }
          }
        }
      } catch (e) {
        // If any unexpected error in parsing, at least log the original error
        console.error('Error while processing GraphQL error:', e);
        if (err.message) {
          errorMessages.push(err.message);
        }
      }
    }

    if (errorMessages.length > 0) {
      console.error('[GraphQL Error Details]:', errorMessages);
    }
  }

  if (networkError) {
    console.error('[Network error]:', networkError);
    // Handle disconnects, timeouts, etc.
  }
});

const removeTypenameLink = removeTypenameFromVariables();

const createApollo = (httpLink: HttpLink) => ({
  link: from([removeTypenameLink, errorLink, httpLink.create({ uri: environment.haulmatic.apiGateway })]),

  // Rate cards are server-side clones of one template and keep its _id, so every
  // schedule carries the same rateStructure._id. Normalised, they collapse into a
  // single cache entity and every row renders the last-written rates. keyFields:
  // false stores each inline with its own schedule; nothing reads these ids.
  cache: new InMemoryCache({
    typePolicies: {
      ProvidedServiceSmartRateCard: { keyFields: false },
      ProvidedServiceSmartRateCardDataTemplate: { keyFields: false },
    },
  }),
  defaultOptions: {
    query: {
      fetchPolicy: 'network-only',
    },
  },
  headers: {
    Authorization: `Bearer ${token}`,
  },
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(APP_ROUTES, withPreloading(PreloadAllModules)),
    provideAnimationsAsync(),
    provideEnvironmentNgxMask(),
    AuthGuardService,
    AngularFirestore,
    {
      provide: APOLLO_OPTIONS,
      useFactory: createApollo,
      deps: [HttpLink],
    },
    Apollo,
    importProvidersFrom([
      CoreModule,
      AngularFireModule.initializeApp(environment.firebaseConfig),
      HttpClientModule,
      FormlyModule.forRoot(COMPONENT_CONFIG),
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      }),
      MatNativeDateModule,
      OwlDateTimeModule,
      OwlNativeDateTimeModule,
    ]),
    { provide: FORMLY_CONFIG, multi: true, useFactory: formlyValidationConfig, deps: [TranslateService] },
  ],
};
